// Design System sizing tokens (Figma DS)
// Each size defines: height, horizontal padding, border-radius, font-size, gap
const sizes = {
    xl: 'py-5 px-3.5 rounded-xl text-base',
    l: 'py-4 px-3 rounded-lg text-sm',
    m: 'py-2.5 px-2.5 rounded-lg text-sm',
    s: 'py-2 px-2 rounded-md text-xs',
};

// Base input styles shared across all sizes
const inputBase =
    'box-border leading-none w-full text-black transition-all placeholder-[#a1a1aa] font-normal relative bg-white outline-none border border-[#d4d4d8] shadow-[0px_1px_5px_0px_rgba(0,0,0,0.08)] focus:!border-black';

const inputDisabledBase =
    'box-border leading-none text-gray-300 transition-all placeholder-gray-300 font-normal relative bg-neutral-50 outline-none border border-gray-200 shadow-[0px_1px_5px_0px_rgba(0,0,0,0.08)] !cursor-not-allowed w-full';

const inputErrorBorder = '!border-[#ff4e4e] !shadow-[0px_1px_5px_0px_rgba(227,0,0,0.08)]';
const inputValidatedBorder = '!border-[#27a476] !shadow-[0px_1px_5px_0px_rgba(18,181,0,0.08)]';

// Sized input classes
const inputSized = {
    xl: `${inputBase} ${sizes.xl}`,
    l: `${inputBase} ${sizes.l}`,
    m: `${inputBase} ${sizes.m}`,
    s: `${inputBase} ${sizes.s}`,
};

const inputDisabledSized = {
    xl: `${inputDisabledBase} ${sizes.xl}`,
    l: `${inputDisabledBase} ${sizes.l}`,
    m: `${inputDisabledBase} ${sizes.m}`,
    s: `${inputDisabledBase} ${sizes.s}`,
};

// Select sizing
const selectBase =
    'appearance-none box-border leading-none w-full text-black transition-all hover:bg-neutral-100 cursor-pointer outline-none border border-[#d4d4d8] shadow-[0px_1px_5px_0px_rgba(0,0,0,0.08)] bg-white';

const selectSized = {
    xl: `${selectBase} ${sizes.xl}`,
    l: `${selectBase} ${sizes.l}`,
    m: `${selectBase} ${sizes.m}`,
    s: `${selectBase} ${sizes.s}`,
};

// Textarea sizing (reuses input base + sizes, adds min-height for textarea look)
const textareaSized = {
    xl: `${inputBase} ${sizes.xl} min-h-[140px] resize-none`,
    l: `${inputBase} ${sizes.l} min-h-[120px] resize-none`,
    m: `${inputBase} ${sizes.m} min-h-[100px] resize-none`,
    s: `${inputBase} ${sizes.s} min-h-[80px] resize-none`,
};

// Label sizing (Bricolage Grotesque Medium)
const labelBase = 'font-bricolage font-medium text-[#52525b] block cursor-pointer tracking-[-0.12px]';
const labelSized = {
    xl: `${labelBase} text-[12px] leading-[12px] pb-[10px]`,
    l: `${labelBase} text-[12px] leading-[12px] pb-[8px]`,
    m: `${labelBase} text-[12px] leading-[11px] pb-[6px]`,
    s: `${labelBase} text-[11px] leading-[11px] pb-[4px]`,
};

// Toggle sizing
const toggleSized = {
    xl: { track: 'w-[40px] h-[24px]', thumb: 'h-5 w-5' },
    l: { track: 'w-[36px] h-[22px]', thumb: 'h-4 w-4' },
    m: { track: 'w-[32px] h-[20px]', thumb: 'h-[14px] w-[14px]' },
    s: { track: 'w-[26px] h-[16px]', thumb: 'h-[10px] w-[10px]' },
};

// Toggle label font sizing
const toggleLabelSized = {
    xl: 'text-[16px]',
    l: 'text-[15px]',
    m: 'text-[14px]',
    s: 'text-[13px]',
};

// Legacy input styles (kept for backwards compatibility)
const input =
    'min-h-[32px] box-border leading-none w-full text-black transition-all placeholder-gray-500 font-thin !rounded-md relative bg-white outline-none !shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] border !border-gray-200 focus:!border-black';
const inputNoShadow = 'w-full text-black transition-all placeholder-gray-500 font-thin rounded-md relative bg-white outline-none text-sm border border-gray-200';
const label = 'text-gray-500 block  cursor-pointer text-xs';
const select =
    'min-h-[32px] appearance-none box-border leading-none w-full text-black transition-all hover:bg-neutral-100 rounded-md cursor-pointer outline-none text-sm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] border border-gray-200 bg-white';
const buttonSelect =
    'min-h-[32px] appearance-none box-border leading-none w-full text-black transition-all font-thin rounded-md relative bg-white outline-none border border-gray-300 focus:border-black cursor-pointer';
const inputXsBase = `${input} p-2 text-xs`;
const inputLegacyBase = `${input} text-sm p-2.5`;
const inputLargeBase = `${input} text-sm p-3`;

const xsModifiers = 'p-2 pl-3 text-xs';
const baseModifiers = 'p-2.5';
const largeModifiers = 'p-2';
const disabledModifiers =
    'text-gray-300 transition-all placeholder-gray-300 font-thin rounded-md border border-gray-200 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] bg-neutral-50 outline-none !cursor-not-allowed';

const form = {
    // New DS sizing system
    sizes,
    input_sized: inputSized,
    input_disabled_sized: inputDisabledSized,
    input_error_border: inputErrorBorder,
    input_validated_border: inputValidatedBorder,
    select_sized: selectSized,
    textarea_sized: textareaSized,
    label_sized: labelSized,
    toggle_sized: toggleSized,
    toggle_label_sized: toggleLabelSized,

    // Legacy styles (unchanged for backwards compatibility)
    select_full_width: `${select} ${baseModifiers} pl-3`,
    select_full_width_xs: `${select} ${xsModifiers} pl-3`,
    select: `${select} ${baseModifiers} pl-3 max-w-input`,
    select_xs: `${select} ${xsModifiers} pl-3 max-w-input`,
    input_full_width: `${inputLegacyBase} ${baseModifiers}`,
    input_full_width_xs: `${inputXsBase} ${xsModifiers}`,
    input: `${inputLegacyBase} ${baseModifiers} max-w-input`,
    input_disabled: `${inputLegacyBase} ${baseModifiers} ${disabledModifiers} w-full max-w-input `,
    input_xs: `${inputXsBase} ${xsModifiers} max-w-input`,
    input_xs_disabled: `${inputXsBase} ${xsModifiers} ${disabledModifiers} w-full max-w-input`,
    input_large: `${inputLargeBase} ${largeModifiers}`,
    button_select: `${buttonSelect} text-sm ${baseModifiers}`,
    button_select_large: `${buttonSelect} text-sm p-3 ${largeModifiers}`,
    input_no_shadow: inputNoShadow,
    input_no_shadow_xs: `${inputNoShadow} ${xsModifiers}`,
    input_no_shadow_base: `${inputNoShadow} ${baseModifiers}`,
    input_no_shadow_base_xs: `${inputNoShadow} ${xsModifiers}`,
    input_no_shadow_large: `${inputNoShadow} ${largeModifiers}`,
    // Invoice line fields: no border/shadow by default, borders revealed when the row is
    // hovered OR being edited (focus-within), black border on the focused field itself.
    // Relies on the parent line row being `group/tabletr`.
    line_input:
        'min-h-[32px] box-border leading-none w-full text-black transition-all placeholder-gray-400 font-thin rounded-md relative bg-transparent outline-none border border-transparent group-hover/tabletr:border-gray-200 group-hover/tabletr:bg-white group-focus-within/tabletr:border-gray-200 group-focus-within/tabletr:bg-white focus:!border-black focus:!bg-white p-2 text-xs',
    line_select:
        'min-h-[32px] appearance-none box-border leading-none w-full text-black transition-all rounded-md cursor-pointer outline-none border border-transparent bg-transparent group-hover/tabletr:border-gray-200 group-hover/tabletr:bg-white group-focus-within/tabletr:border-gray-200 group-focus-within/tabletr:bg-white focus:!border-black focus:!bg-white p-2 pl-3 text-xs',
    textarea: `${inputBase} w-full text-black placeholder-gray-500 font-thin py-2 px-2 lg:py-3 lg:px-2 rounded-md relative bg-white outline-none text-xs lg:text-sm min-h-[120px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] before:content-[""] before:absolute before:inset-[-1px] before:rounded-md before:bg-[linear-gradient(360deg,#D3D2D2_0%,#E7E7E7_91.67%)] before:z-[-1] before:pointer-events-none resize-none`,
    textarea_notes: `${inputBase} text-sm text-gray-800 min-h-[140px] !bg-[#F8F3E0] px-3 py-2 rounded w-full h-[200px]`,
    label_inline: `${label} inline-block`,
    label,
    labelFlow: 'block text-xs font-medium text-[#707070] font-inter whitespace-nowrap mb-1 text-left',
    label_xs: `${label} text-[10px]`,
    horizontal_label: 'text-gray-500 font-normal text-sm cursor-pointer',
    helper: 'block text-gray-500 font-thin text-sm',
    error: 'block text-xs text-red-600 mt-[4px]',
    filled_ai: `${inputBase} rounded border-image-ai-gradient`,
    containers: {
        element: 'mb-2 lg:mb-3 flex flex-wrap',
        input: 'w-full md:flex-auto',
        label: 'w-full',
        submit: 'flex justify-stretch w-full space-x-2',
    },
    inbound: {
        input: 'w-full h-input-lg px-3 rounded-input bg-white border border-inbound-border1 shadow-input text-inbound-fg2 text-base leading-tight outline-none transition-all placeholder:text-inbound-fg6 hover:border-[#B6B6BD] focus:border-inbound-fg2 focus:shadow-[0_0_0_3px_rgba(0,0,0,0.06)] disabled:bg-inbound-bg2 disabled:text-inbound-fg5 disabled:cursor-not-allowed',
        select: 'w-full h-input-lg px-3 rounded-input bg-white border border-inbound-border1 shadow-input text-inbound-fg2 text-base outline-none cursor-pointer hover:border-[#B6B6BD]',
        label: 'text-sm font-medium tracking-[-0.02em] text-inbound-fg3',
        error: 'block text-sm text-inbound-danger mt-1',
        containers: { element: 'flex flex-col gap-2.5', input: 'w-full', label: 'w-full' },
    },
    filters: {
        search: {
            input: 'w-full py-2 px-3 lg:py-4 lg:px-6 rounded-lg bg-white border border-gray-200 dark:border-gray-900 hover:shadow focus:shadow-lg text-gray-700 text-xs lg:text-sm lg:text-base font-thin outline-none focus:!border-black',
            icon: 'fas fa-search outline-none text-gray-800 absolute center-y right-0 mr-4 lg:mr-8 text-xs lg:text-sm lg:text-base',
        },
        select: {
            input: 'w-full py-2 px-3 lg:py-4 lg:px-6 pr-8 lg:pr-10 rounded-lg bg-white border border-gray-200 dark:border-gray-900 hover:shadow focus:shadow-lg text-gray-500 text-xs lg:text-sm lg:text-base font-thin outline-none focus:!border-black',
            icon: 'fas fa-arrow-down text-gray-800 absolute center-y right-0 mr-4 lg:mr-6 text-sm',
        },
    },
};

export default form;
